import { useTheme } from "@mui/material";
import dayjs from "dayjs";
import { atom, useAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useWebSocket, { ReadyState } from "react-use-websocket";
import * as DeepchatApi from "../../api/deepchat";
import {
  ChatResponse,
  ChatResponseAnswerPartial,
  ClarifyingQuestionsResponse,
  SourceDocument,
  ThinkingStepData,
} from "../../components/Deepchat/models";
import {
  QuestionCanAcceptFeedback,
  QuestionIsLoadingAtom,
  QuestionTextAtom,
  SessionThreadAtom,
  ThinkingStepsAtom,
  ThinkingStepsCollapsedAtom,
} from "../../components/Deepchat/state/DeepchatState";
import useDeepchat, { WebSocketChatResponse } from "../../contexts/useDeepchat";
import emitSnackbar from "../../emitSnackbar";
import { useHideFilterDrawer } from "../../pages/AppLayout";
import useUserContext from "../../components/Authorization/useUserContext";
import { historyRefreshAtom } from "./useRecentThreads";

const DEEPCHAT_WS_URI = import.meta.env.VITE_APP_DEEPCHAT_WS_URI;

//ATOMS
export const hideFilterAtom = atom(true);
export const disableFilterAtom = atom(false);
export const socketSeedAtom = atom(0);

export const useDeepChatStore = () => {
  const theme = useTheme();
  const user = useUserContext();
  const navigate = useNavigate();
  const { hideFilterDrawer, setHideFilterDrawer } = useHideFilterDrawer();
  const marginTransition = `margin ${theme.transitions.duration.enteringScreen}ms`;
  const Deepchat = useDeepchat();

  //atoms
  const [disableFilters, setDisableFilters] = useAtom(disableFilterAtom);
  const [questionIsLoading, setQuestionIsLoading] = useAtom(QuestionIsLoadingAtom);
  const [sessionThread, setSessionThread] = useAtom(SessionThreadAtom);
  const [, setAllowFeedback] = useAtom(QuestionCanAcceptFeedback);
  const [, setQuestion] = useAtom(QuestionTextAtom);
  const [, setThinkingSteps] = useAtom(ThinkingStepsAtom);
  const [, setHistoryRefresh] = useAtom(historyRefreshAtom);
  const [, setThinkingStepsCollapsed] = useAtom(ThinkingStepsCollapsedAtom);

  const shouldDisableFilters = sessionThread.length > 0 || questionIsLoading;

  // -- Thread State Management
  // -- is this required? As we can directly pass 0
  // const [socketSeed, setSocketSeed] = useAtom(socketSeedAtom);

  const formatChatUrl = (chatAuthCode: string) => {
    return `${DEEPCHAT_WS_URI}?authCode=${chatAuthCode}`;
  };

  //websocket
  const getChartUrl = useCallback(async () => {
    const response = await DeepchatApi.getToken(user);
    return formatChatUrl(response.auth_code);
  }, []);

  const getWebSocket = useCallback(() => useWebSocket<WebSocketChatResponse<unknown>>(getChartUrl), [0]);
  const { lastJsonMessage, sendJsonMessage, readyState } = getWebSocket();

  if (shouldDisableFilters != disableFilters) {
    setDisableFilters(shouldDisableFilters);
  }

  const toggleHideFilter = (value?: boolean) => {
    if (value === undefined) {
      setHideFilterDrawer((_) => !_);
    } else {
      setHideFilterDrawer(value);
    }
  };

  //to log websocket data
  useEffect(() => {
    console.debug(
      `[${dayjs().format("HH:mm:ss.SSS")}] The WebSocket is currently: ${SocketConnectionStatus[readyState]}`,
    );
  }, [readyState]);

  // Drawer expansion is managed by DeepchatPage via SidebarDrawerExpandedAtom.
  // The TopBar hamburger toggles hideFilterDrawer, which DeepchatPage syncs.

  // This is handling both web socket messages and page state transitions, but I cant tell if its in the wrong place
  useEffect(() => {
    switch (lastJsonMessage?.type) {
      case "chat.thinking_step": {
        const message = lastJsonMessage as WebSocketChatResponse<ThinkingStepData>;
        setAllowFeedback(false); //Reset feedback gate so SessionResponseItem detects this as the active response

        setThinkingSteps((prev) => [
          ...prev.map((s) => (s.status === "active" ? { ...s, status: "completed" as const } : s)),
          {
            step: message.data.step,
            label: message.data.label,
            detail: message.data.detail,
            metadata: message.data.metadata,
            status: "active" as const,
            timestamp: Date.now(),
          },
        ]);
        break;
      }
      case "chat.initial": {
        const message = lastJsonMessage as WebSocketChatResponse<ChatResponse>;
        const session = Deepchat.getKeyFromSession([message.data]);
        navigate(`/deepchat/threads/${session}`, { replace: true, state: { prevent_load: true } });
        // Replace the optimistic pending item (added on submit) with the real payload so the
        // question doesn't briefly duplicate. Falls back to appending if none is pending.
        setSessionThread((_session) => {
          const last = _session.at(-1);
          if (last?.pending) {
            const next = [..._session];
            next[next.length - 1] = message.data;
            return next;
          }
          return [..._session, message.data];
        });

        setQuestion("");
        break;
      }
      case "chat.answer": {
        const message = lastJsonMessage as WebSocketChatResponse<ChatResponseAnswerPartial>;

        if (questionIsLoading) {
          setQuestionIsLoading(false);
          // First answer chunk: collapse the thinking-steps dropdown now that the response
          // is starting to stream, so it gives way to the answer.
          setThinkingStepsCollapsed(true);
        }

        // Mark remaining active thinking steps as completed (answer text has started)
        setThinkingSteps((prev) =>
          prev.map((s) => (s.status === "active" ? { ...s, status: "completed" as const } : s)),
        );

        setSessionThread((_session) => {
          const newSessionArray = [..._session];
          const lastInteraction = newSessionArray.at(-1);
          if (lastInteraction && message) {
            lastInteraction.response = message.data.answer;
          }
          return newSessionArray;
        });
        break;
      }
      case "chat.source": {
        const message = lastJsonMessage as WebSocketChatResponse<SourceDocument>;
        setSessionThread((_session) => {
          const newSessionArray = [..._session];
          const lastInteraction = newSessionArray.at(-1);
          if (lastInteraction && message) {
            lastInteraction.sources = [...(lastInteraction.sources ?? []), message.data];
          }
          return newSessionArray;
        });
        break;
      }
      case "chat.clarify": {
        const message = lastJsonMessage as WebSocketChatResponse<ClarifyingQuestionsResponse>;

        if (questionIsLoading) {
          setQuestionIsLoading(false);
        }
        // Clarification / enhanced prompts are appearing — collapse the thinking-steps
        // dropdown so the module takes focus.
        setThinkingStepsCollapsed(true);

        // The backend won't stream an answer for a clarification turn, so close out any
        // in-flight thinking steps and attach the questions to the current interaction.
        setThinkingSteps((prev) =>
          prev.map((s) => (s.status === "active" ? { ...s, status: "completed" as const } : s)),
        );

        setSessionThread((_session) => {
          const newSessionArray = [..._session];
          const lastInteraction = newSessionArray.at(-1);
          if (lastInteraction && message) {
            lastInteraction.clarification = message.data;
          }
          return newSessionArray;
        });
        break;
      }
      case "chat.complete": {
        setAllowFeedback(true);

        // Refresh the recent-threads list so a newly created thread appears
        // without requiring a manual page refresh.
        setHistoryRefresh((n) => n + 1);

        // Persist thinking steps into the ChatResponse and reset for next interaction
        setThinkingSteps((currentSteps) => {
          //so ti will be reset for next question but viewable in the same thread
          if (currentSteps.length > 0) {
            setSessionThread((_session) => {
              const newSessionArray = [..._session];
              const lastInteraction = newSessionArray.at(-1);
              if (lastInteraction) {
                lastInteraction.thinkingSteps = currentSteps.map((s) =>
                  s.status === "active" ? { ...s, status: "completed" as const } : s,
                );
              }
              return newSessionArray;
            });
          }
          return [];
        });
        break;
      }
      default: {
        if (lastJsonMessage?.type === "chat.exception") {
          setThinkingSteps((prev) =>
            prev.map((s) => (s.status === "active" ? { ...s, status: "failed" as const } : s)),
          );
          setQuestionIsLoading(false);
          emitSnackbar("Something went wrong, please try again in a moment", "warning");
        } else if ((lastJsonMessage as any)?.message === "Internal server error") {
          setQuestionIsLoading(false);
          emitSnackbar("Something went wrong, please try again in a moment", "warning");
        }
      }
    }
  }, [lastJsonMessage]);

  return {
    hideFilterDrawer,
    toggleHideFilter,
    disableFilters,
    lastJsonMessage,
    marginTransition,
    sendJsonMessage,
    readyState,
  };
};

const SocketConnectionStatus = {
  [ReadyState.CONNECTING]: "Connecting",
  [ReadyState.OPEN]: "Open",
  [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  [ReadyState.CLOSING]: "Closing",
  [ReadyState.CLOSED]: "Closed",
};
